/**
 * OnlineCoopSystem — Manages the WebSocket connection for online 2-player co-op.
 *
 * Architecture:
 *   - P1 (host) creates a room and gets a 4-char room code.
 *   - P2 (guest) joins using that code.
 *   - Host sends STATE_UPDATE snapshots; both players send INPUT_EVENT messages.
 *   - All co-op logic is purely additive — single-player and local co-op are unaffected.
 *
 * Callbacks (set before calling connect()):
 *   onRoomCreated(roomCode)          — Host: room was created, code is ready to share
 *   onGuestJoined()                  — Host: guest connected, game can be started
 *   onRoomJoined(roomCode)           — Guest: successfully joined a room
 *   onGameStarted()                  — Both: host pressed Start
 *   onInputReceived(playerId, state) — Incoming remote input event
 *   onStateReceived(snapshot)        — Guest: incoming world-state snapshot from host
 *   onError(message)                 — Server sent ROOM_ERROR
 *   onDisconnected()                 — Remote player disconnected mid-game
 *
 * @module OnlineCoopSystem
 */

import DebugLog from '../debug/DebugLog.js';

const ROOM_CODE_RE = /^[A-Z0-9]{4}$/i;

const OnlineCoopSystem = {
  /** @type {WebSocket|null} */
  socket: null,

  /** @type {string|null} */
  roomCode: null,

  /** @type {boolean} */
  isHost: false,

  /** @type {boolean} */
  isConnected: false,

  /** @type {boolean} */
  guestPresent: false,

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  /** @type {((roomCode: string) => void)|null} */
  onRoomCreated: null,

  /** @type {(() => void)|null} */
  onGuestJoined: null,

  /** @type {((roomCode: string) => void)|null} */
  onRoomJoined: null,

  /** @type {(() => void)|null} */
  onGameStarted: null,

  /** @type {((playerId: number, inputState: object) => void)|null} */
  onInputReceived: null,

  /** @type {((snapshot: object) => void)|null} */
  onStateReceived: null,

  /** @type {((message: string) => void)|null} */
  onError: null,

  /** @type {(() => void)|null} */
  onDisconnected: null,

  // ─── Connection ────────────────────────────────────────────────────────────

  /**
   * Connect to the WebSocket server.
   * Resolves immediately; the socket connects asynchronously.
   * If the server is unavailable the socket will emit an error and
   * isConnected will remain false — no exception is thrown.
   *
   * @param {string} [serverUrl='ws://localhost:3001'] — WebSocket server URL
   * @returns {Promise<void>} Resolves when connected (or fails gracefully)
   */
  connect(serverUrl = 'ws://localhost:3001') {
    return new Promise((resolve) => {
      if (this.socket && this.isConnected) {
        DebugLog.warn('[online] connect() called but already connected');
        resolve();
        return;
      }

      DebugLog.info(`[online] Connecting to ${serverUrl}/ws`);

      let ws;
      try {
        ws = new WebSocket(`${serverUrl}/ws`);
      } catch (err) {
        DebugLog.error(`[online] WebSocket constructor threw: ${err.message}`);
        resolve(); // Graceful degradation — caller checks isConnected
        return;
      }

      this.socket = ws;

      ws.addEventListener('open', () => {
        this.isConnected = true;
        DebugLog.info('[online] WebSocket connected');
        resolve();
      });

      ws.addEventListener('message', (event) => {
        this._handleMessage(event.data);
      });

      ws.addEventListener('close', () => {
        const wasConnected = this.isConnected;
        this.isConnected = false;
        DebugLog.info('[online] WebSocket closed');
        if (wasConnected && this.onDisconnected) {
          this.onDisconnected();
        }
      });

      ws.addEventListener('error', (err) => {
        DebugLog.warn(`[online] WebSocket error — online play unavailable`);
        this.isConnected = false;
        resolve(); // Graceful degradation
      });

      // Timeout — if connection doesn't open within 5s, resolve gracefully
      const timeout = setTimeout(() => {
        if (!this.isConnected) {
          DebugLog.warn('[online] WebSocket connection timed out');
          ws.close();
          resolve();
        }
      }, 5000);

      ws.addEventListener('open', () => clearTimeout(timeout), { once: true });
    });
  },

  /**
   * Disconnect and clean up.
   */
  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.isConnected = false;
    this.roomCode = null;
    this.isHost = false;
    this.guestPresent = false;
    DebugLog.info('[online] Disconnected and cleaned up');
  },

  // ─── Room management ───────────────────────────────────────────────────────

  /**
   * Create a new room. Caller becomes the host.
   * onRoomCreated(code) fires when the server responds.
   */
  createRoom() {
    if (!this.isConnected) {
      DebugLog.warn('[online] createRoom() called but not connected');
      return;
    }
    this.isHost = true;
    this._send({ type: 'CREATE_ROOM' });
    DebugLog.info('[online] CREATE_ROOM sent');
  },

  /**
   * Join an existing room by code. Caller becomes the guest.
   * onRoomJoined(code) fires on success; onError fires on failure.
   *
   * @param {string} code — 4-character alphanumeric room code
   */
  joinRoom(code) {
    if (!this.isConnected) {
      DebugLog.warn('[online] joinRoom() called but not connected');
      if (this.onError) this.onError('Not connected to server');
      return;
    }

    const normalised = String(code ?? '').toUpperCase().trim();
    if (!ROOM_CODE_RE.test(normalised)) {
      DebugLog.warn(`[online] joinRoom() invalid code format: "${code}"`);
      if (this.onError) this.onError('Room code must be 4 alphanumeric characters');
      return;
    }

    this.isHost = false;
    this._send({ type: 'JOIN_ROOM', code: normalised });
    DebugLog.info(`[online] JOIN_ROOM sent; code=${normalised}`);
  },

  /**
   * Host-only: start the game once the guest has joined.
   */
  startGame() {
    if (!this.isHost) {
      DebugLog.warn('[online] startGame() called by non-host');
      return;
    }
    this._send({ type: 'START_GAME' });
    DebugLog.info('[online] START_GAME sent');
  },

  // ─── Sync ──────────────────────────────────────────────────────────────────

  /**
   * Send a player input event to the remote player.
   * @param {number} playerId — 1 or 2
   * @param {object} inputState — Snapshot of input state
   */
  sendInput(playerId, inputState) {
    this._send({ type: 'INPUT_EVENT', playerId, inputState: inputState ?? {} });
  },

  /**
   * Host-only: broadcast a world-state snapshot to the guest.
   * @param {object} snapshot — Serializable game state
   */
  sendStateUpdate(snapshot) {
    if (!this.isHost) {
      DebugLog.warn('[online] sendStateUpdate() called by non-host');
      return;
    }
    this._send({ type: 'STATE_UPDATE', snapshot: snapshot ?? {} });
  },

  // ─── Validation helpers (also useful for tests) ───────────────────────────

  /**
   * Validate that a room code string is correctly formatted.
   * @param {string} code
   * @returns {boolean}
   */
  isValidRoomCode(code) {
    return ROOM_CODE_RE.test(String(code ?? ''));
  },

  // ─── Internal ──────────────────────────────────────────────────────────────

  /**
   * @param {object} msg
   */
  _send(msg) {
    if (!this.socket || !this.isConnected) {
      DebugLog.warn(`[online] _send() dropped (not connected): ${msg.type}`);
      return;
    }
    this.socket.send(JSON.stringify(msg));
  },

  /**
   * Handle incoming message from server.
   * @param {string} raw — Raw JSON string
   */
  _handleMessage(raw) {
    let msg;
    try {
      msg = JSON.parse(raw);
    } catch {
      DebugLog.warn('[online] Received unparseable message from server');
      return;
    }

    DebugLog.info(`[online] Message received; type=${msg.type}`);

    switch (msg.type) {
      case 'ROOM_CREATED':
        this.roomCode = msg.code;
        DebugLog.info(`[online] Room created; code=${msg.code}`);
        if (this.onRoomCreated) this.onRoomCreated(msg.code);
        break;

      case 'GUEST_JOINED':
        this.guestPresent = true;
        DebugLog.info('[online] Guest joined room');
        if (this.onGuestJoined) this.onGuestJoined();
        break;

      case 'ROOM_JOINED':
        this.roomCode = msg.code;
        DebugLog.info(`[online] Joined room; code=${msg.code}`);
        if (this.onRoomJoined) this.onRoomJoined(msg.code);
        break;

      case 'GAME_STARTED':
        DebugLog.info('[online] Game started by host');
        if (this.onGameStarted) this.onGameStarted();
        break;

      case 'INPUT_EVENT':
        if (this.onInputReceived) {
          this.onInputReceived(msg.playerId, msg.inputState ?? {});
        }
        break;

      case 'STATE_UPDATE':
        if (this.onStateReceived) {
          this.onStateReceived(msg.snapshot ?? {});
        }
        break;

      case 'ROOM_ERROR':
        DebugLog.warn(`[online] Room error: ${msg.message}`);
        if (this.onError) this.onError(msg.message ?? 'Unknown error');
        break;

      case 'PLAYER_DISCONNECTED':
        DebugLog.info('[online] Remote player disconnected');
        this.guestPresent = false;
        if (this.onDisconnected) this.onDisconnected();
        break;

      default:
        DebugLog.warn(`[online] Unknown message type: ${msg.type}`);
        break;
    }
  },
};

export default OnlineCoopSystem;
